/**
 * Backstage Contacts — mobile-only, Apple-Contacts-style address book.
 *
 * Rendered from talent-contacts.tsx when useIsMobile() is true. Desktop is
 * untouched. Reuses the same query key + CRUD endpoints as the desktop page.
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useOwnedTalents } from "@/hooks/use-owned-talents";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { TalentContact } from "@shared/schema";
import {
  Loader2,
  Plus,
  Search,
  Star,
  Phone,
  MessageSquare,
  Mail,
  Navigation,
  Pencil,
  Trash2,
  Download,
  X,
  ChevronLeft,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  initials,
  avatarSwatch,
  roleLabel,
  directionsUrl,
  contactVCard,
  CONTACT_ROLES,
} from "./lib";

type ContactForm = {
  name: string;
  role: string;
  email: string;
  phone: string;
  company: string;
  address: string;
  notes: string;
  isFavorite: boolean;
};

const EMPTY_FORM: ContactForm = {
  name: "",
  role: "other",
  email: "",
  phone: "",
  company: "",
  address: "",
  notes: "",
  isFavorite: false,
};

function Avatar({ name, size = "md" }: { name?: string | null; size?: "md" | "lg" }) {
  const dim = size === "lg" ? "h-20 w-20 text-2xl" : "h-11 w-11 text-sm";
  return (
    <div className={`flex ${dim} shrink-0 items-center justify-center rounded-full font-semibold ${avatarSwatch(name)}`}>
      {initials(name)}
    </div>
  );
}

export default function BackstageContacts() {
  const { toast } = useToast();
  const { ownedTalents, selectedTalentId, setSelectedTalentId, isLoading: talentLoading } =
    useOwnedTalents();

  const { data: contacts = [], isLoading: contactsLoading } = useQuery<TalentContact[]>({
    queryKey: ["/api/talent-contacts", selectedTalentId],
    queryFn: async () => {
      const r = await fetch(`/api/talent-contacts?talentId=${encodeURIComponent(selectedTalentId!)}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("failed to load contacts");
      return r.json();
    },
    enabled: !!selectedTalentId,
  });

  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState<TalentContact | null>(null);
  const [editor, setEditor] = useState<{ open: boolean; editing: TalentContact | null }>({
    open: false,
    editing: null,
  });
  const [form, setForm] = useState<ContactForm>(EMPTY_FORM);

  const { favorites, groups } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = (contacts || [])
      .filter((c) => {
        if (!q) return true;
        return [c.name, c.company, c.email, c.phone, roleLabel(c.role)]
          .filter(Boolean)
          .some((v) => (v as string).toLowerCase().includes(q));
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

    const favs = matched.filter((c) => c.isFavorite);
    const byLetter: Record<string, TalentContact[]> = {};
    for (const c of matched) {
      const first = (c.name || "#").trim()[0]?.toUpperCase() || "#";
      const key = /[A-Z]/.test(first) ? first : "#";
      (byLetter[key] ||= []).push(c);
    }
    const ordered = Object.keys(byLetter)
      .sort((a, b) => (a === "#" ? 1 : b === "#" ? -1 : a.localeCompare(b)))
      .map((letter) => ({ letter, items: byLetter[letter] }));
    return { favorites: favs, groups: ordered };
  }, [contacts, search]);

  function openEditor(c: TalentContact | null) {
    setForm(
      c
        ? {
            name: c.name || "",
            role: (c.role as string) || "other",
            email: c.email || "",
            phone: c.phone || "",
            company: c.company || "",
            address: c.address || "",
            notes: c.notes || "",
            isFavorite: c.isFavorite,
          }
        : EMPTY_FORM,
    );
    setEditor({ open: true, editing: c });
  }

  const upsert = useMutation({
    mutationFn: async () => {
      if (!selectedTalentId) throw new Error("no talent selected");
      if (editor.editing) {
        const r = await apiRequest("PATCH", `/api/talent-contacts/${editor.editing.id}`, form);
        return r.json();
      }
      const r = await apiRequest("POST", "/api/talent-contacts", { talentId: selectedTalentId, ...form });
      return r.json();
    },
    onSuccess: (saved: TalentContact) => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent-contacts", selectedTalentId] });
      setEditor({ open: false, editing: null });
      if (detail) setDetail(saved);
      toast({ title: "Saved", description: "Contact saved." });
    },
    onError: (err: any) =>
      toast({ title: "Couldn't save", description: err?.message, variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/talent-contacts/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent-contacts", selectedTalentId] });
      setDetail(null);
      toast({ title: "Removed", description: "Contact removed." });
    },
  });

  const toggleFav = useMutation({
    mutationFn: async (c: TalentContact) => {
      const r = await apiRequest("PATCH", `/api/talent-contacts/${c.id}`, { isFavorite: !c.isFavorite });
      return r.json();
    },
    onSuccess: (saved: TalentContact) => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent-contacts", selectedTalentId] });
      if (detail && detail.id === saved.id) setDetail(saved);
    },
  });

  function saveToPhone(c: TalentContact) {
    const blob = new Blob([contactVCard(c)], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(c.name || "contact").replace(/[^a-z0-9]+/gi, "-")}.vcf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  if (talentLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="loading-contacts">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (ownedTalents.length === 0) {
    return (
      <div className="min-h-screen bg-background px-5 pt-10">
        <p className="text-sm text-muted-foreground">
          You don&apos;t have a talent profile linked yet. Ask an admin to link you.
        </p>
      </div>
    );
  }

  const total = contacts?.length || 0;

  return (
    <div className="min-h-screen bg-background" data-testid="backstage-contacts">
      {/* ── Header ───────────────────────────────────────────────── */}
      <header className="px-5 pt-8 pb-3">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">Backstage</p>
            <h1 className="mt-1 text-[26px] font-semibold tracking-tight text-foreground">Contacts</h1>
          </div>
          <button
            onClick={() => openEditor(null)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground active:scale-95 transition-transform"
            aria-label="Add contact"
            data-testid="button-add-contact"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>

        {ownedTalents.length > 1 && (
          <div className="mt-3">
            <Select value={selectedTalentId ?? ""} onValueChange={setSelectedTalentId}>
              <SelectTrigger className="h-9 w-full rounded-full border-border bg-card" data-testid="select-talent">
                <SelectValue placeholder="Select talent" />
              </SelectTrigger>
              <SelectContent>
                {ownedTalents.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contacts"
            className="h-10 rounded-full border-border bg-card pl-9"
            data-testid="input-search-contacts"
          />
        </div>
      </header>

      {/* ── List ─────────────────────────────────────────────────── */}
      {contactsLoading ? (
        <div className="flex justify-center pt-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : total === 0 ? (
        <div className="px-5 pt-16 text-center">
          <p className="text-sm text-muted-foreground">No contacts yet.</p>
          <p className="text-xs text-muted-foreground/70">Add the people you work with on the road.</p>
        </div>
      ) : (
        <div className="pb-12">
          {favorites.length > 0 && !search && (
            <Section title="Favorites">
              {favorites.map((c) => (
                <ContactRow key={`f-${c.id}`} c={c} onOpen={() => setDetail(c)} />
              ))}
            </Section>
          )}
          {groups.map((g) => (
            <Section key={g.letter} title={g.letter}>
              {g.items.map((c) => (
                <ContactRow key={c.id} c={c} onOpen={() => setDetail(c)} />
              ))}
            </Section>
          ))}
          {groups.length === 0 && (
            <p className="px-5 pt-10 text-center text-sm text-muted-foreground" data-testid="text-no-contacts">
              No contacts match “{search}”.
            </p>
          )}
        </div>
      )}

      {/* ── Detail sheet ─────────────────────────────────────────── */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent side="bottom" className="max-h-[88vh] overflow-y-auto rounded-t-3xl px-0" data-testid="sheet-contact-detail">
          {detail && (
            <div className="px-5 pb-10">
              <div className="flex flex-col items-center pt-2 text-center">
                <Avatar name={detail.name} size="lg" />
                <h2 className="mt-3 text-xl font-semibold tracking-tight text-foreground">{detail.name}</h2>
                <span className="mt-1 inline-flex items-center rounded-full bg-foreground/[0.06] px-3 py-1 text-xs font-medium text-foreground/70">
                  {roleLabel(detail.role)}
                </span>
                {detail.company && <p className="mt-1 text-sm text-muted-foreground">{detail.company}</p>}
              </div>

              <div className="mt-6 grid grid-cols-4 gap-2">
                <QuickAction disabled={!detail.phone} href={detail.phone ? `tel:${detail.phone}` : undefined} icon={Phone} label="Call" />
                <QuickAction disabled={!detail.phone} href={detail.phone ? `sms:${detail.phone}` : undefined} icon={MessageSquare} label="Text" />
                <QuickAction disabled={!detail.email} href={detail.email ? `mailto:${detail.email}` : undefined} icon={Mail} label="Email" />
                <QuickAction
                  disabled={!detail.address}
                  href={directionsUrl(detail.address) || undefined}
                  external
                  icon={Navigation}
                  label="Map"
                />
              </div>

              <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
                {detail.phone && <Field label="phone" value={detail.phone} href={`tel:${detail.phone}`} />}
                {detail.email && <Field label="email" value={detail.email} href={`mailto:${detail.email}`} />}
                {detail.address && <Field label="address" value={detail.address} />}
                {detail.notes && <Field label="notes" value={detail.notes} />}
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <button
                  onClick={() => toggleFav.mutate(detail)}
                  className="flex items-center justify-center gap-2 rounded-xl border border-border bg-card py-3 text-sm font-medium text-foreground active:scale-[0.98] transition-transform"
                  data-testid="button-favorite-contact"
                >
                  <Star className={detail.isFavorite ? "h-4 w-4 fill-primary text-primary" : "h-4 w-4"} />
                  {detail.isFavorite ? "Favorited" : "Favorite"}
                </button>
                <button
                  onClick={() => saveToPhone(detail)}
                  className="flex items-center justify-center gap-2 rounded-xl border border-border bg-card py-3 text-sm font-medium text-foreground active:scale-[0.98] transition-transform"
                  data-testid="button-save-vcard"
                >
                  <Download className="h-4 w-4" /> Save
                </button>
                <button
                  onClick={() => openEditor(detail)}
                  className="flex items-center justify-center gap-2 rounded-xl border border-border bg-card py-3 text-sm font-medium text-foreground active:scale-[0.98] transition-transform"
                  data-testid="button-edit-contact"
                >
                  <Pencil className="h-4 w-4" /> Edit
                </button>
                <button
                  onClick={() => remove.mutate(detail.id)}
                  disabled={remove.isPending}
                  className="flex items-center justify-center gap-2 rounded-xl border border-border bg-card py-3 text-sm font-medium text-destructive active:scale-[0.98] transition-transform"
                  data-testid="button-delete-contact"
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ── Editor sheet ─────────────────────────────────────────── */}
      <Sheet open={editor.open} onOpenChange={(o) => setEditor((s) => ({ ...s, open: o }))}>
        <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto rounded-t-3xl" data-testid="sheet-contact-editor">
          <div className="mb-4 flex items-center justify-between">
            <button onClick={() => setEditor({ open: false, editing: null })} className="flex items-center gap-1 text-sm text-muted-foreground" data-testid="button-cancel-contact">
              <ChevronLeft className="h-4 w-4" /> Cancel
            </button>
            <h2 className="text-base font-semibold text-foreground">{editor.editing ? "Edit" : "New Contact"}</h2>
            <button
              onClick={() => upsert.mutate()}
              disabled={!form.name.trim() || upsert.isPending}
              className="text-sm font-semibold text-primary disabled:opacity-40"
              data-testid="button-save-contact"
            >
              {upsert.isPending ? "Saving…" : "Done"}
            </button>
          </div>
          <div className="space-y-3.5 pb-8">
            <div className="space-y-1">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-contact-name" />
            </div>
            <div className="space-y-1">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="select-contact-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="input-contact-phone" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="email">Email</Label>
              <Input id="email" inputMode="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-contact-email" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="company">Company</Label>
              <Input id="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="input-contact-company" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="address">Address</Label>
              <Input id="address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="input-contact-address" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="input-contact-notes" />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch id="fav" checked={form.isFavorite} onCheckedChange={(v) => setForm({ ...form, isFavorite: v })} data-testid="switch-contact-favorite" />
              <Label htmlFor="fav" className="text-sm">Favorite</Label>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="sticky top-0 z-10 bg-background/90 px-5 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 backdrop-blur">
        {title}
      </div>
      <div className="px-5">
        <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
          {children}
        </div>
      </div>
      <div className="h-3" />
    </div>
  );
}

function ContactRow({ c, onOpen }: { c: TalentContact; onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      className="flex w-full items-center gap-3 px-3.5 py-2.5 text-left active:bg-foreground/[0.03]"
      data-testid={`row-contact-${c.id}`}
    >
      <Avatar name={c.name} />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 truncate text-[15px] font-medium text-foreground">
          {c.isFavorite && <Star className="h-3 w-3 shrink-0 fill-primary text-primary" />}
          {c.name}
        </p>
        <p className="truncate text-xs text-muted-foreground">{roleLabel(c.role)}{c.company ? ` · ${c.company}` : ""}</p>
      </div>
    </button>
  );
}

function QuickAction({
  icon: Icon,
  label,
  href,
  external,
  disabled,
}: {
  icon: React.ElementType;
  label: string;
  href?: string;
  external?: boolean;
  disabled?: boolean;
}) {
  const inner = (
    <div
      className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-border bg-card py-3 ${
        disabled ? "opacity-35" : "active:scale-[0.97] transition-transform"
      }`}
    >
      <Icon className="h-5 w-5 text-primary" />
      <span className="text-[11px] font-medium text-foreground/80">{label}</span>
    </div>
  );
  if (disabled || !href) return inner;
  return (
    <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined} data-testid={`action-${label.toLowerCase()}`}>
      {inner}
    </a>
  );
}

function Field({ label, value, href }: { label: string; value: string; href?: string }) {
  const body = (
    <div className="px-4 py-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">{label}</p>
      <p className={`mt-0.5 break-words text-sm ${href ? "text-primary" : "text-foreground"}`}>{value}</p>
    </div>
  );
  return href ? <a href={href}>{body}</a> : body;
}
